import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardList, CheckCircle2, Package, Laptop, Building2, Repeat, type LucideIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI, ProjectData } from '../api/projectAPI';
import { categoryLabel } from '../i18n/categoryNames';
import './ProjectCreatePage.css';

interface ProjectFormData {
  // 1단계
  category: string;
  subcategory: string;
  
  // 2단계
  workType: 'outsourcing' | 'remote' | 'onsite' | 'hybrid' | '';
  
  // 3단계
  title: string;
  shortDescription: string;
  
  // 4단계
  budgetMin: number;
  budgetMax: number;
  budgetType: 'project' | 'hourly' | 'negotiable' | 'fixed';
  startDate: string;
  durationDays: number;
  
  // 5단계 (선택)
  description: string;
  requiredSkills: string[];
  attachments: string[];
  experienceLevel: 'junior' | 'intermediate' | 'senior' | '';
}

const ProjectCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  console.log('ProjectCreatePage 렌더링, 사용자:', user);

  // 폼 데이터
  const [formData, setFormData] = useState<ProjectFormData>({
    category: '',
    subcategory: '',
    workType: '',
    title: '',
    shortDescription: '',
    budgetMin: 0,
    budgetMax: 0,
    budgetType: 'fixed',
    startDate: '',
    durationDays: 0,
    description: '',
    requiredSkills: [],
    attachments: [],
    experienceLevel: '',
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // 카테고리 데이터
  const categories = [
    { 
      value: 'design', 
      label: '디자인',
      subcategories: ['로고·브랜딩', '상세·이벤트페이지', 'PPT·인포그래픽', '캐릭터·이모티콘']
    },
    { 
      value: 'development', 
      label: '개발·프로그래밍',
      subcategories: ['웹사이트', '모바일앱', '프로그램', '블록체인']
    },
    { 
      value: 'marketing', 
      label: '마케팅',
      subcategories: ['SNS 마케팅', '광고 운영', 'SEO', '바이럴 마케팅']
    },
    { 
      value: 'writing', 
      label: '글쓰기·번역',
      subcategories: ['블로그·콘텐츠', '번역', '네이밍', '원고·자료 작성']
    },
    { 
      value: 'video', 
      label: '영상·사진',
      subcategories: ['영상 편집', '영상 촬영', '사진 촬영', '사진 편집']
    },
    { 
      value: 'music', 
      label: '음악·오디오',
      subcategories: ['작곡·편곡', '보컬·가이드', '믹싱·마스터링', '더빙·내레이션']
    },
    { 
      value: 'business', 
      label: '비즈니스 컨설팅',
      subcategories: ['법률 자문', '세무·회계', '특허·상표', 'HR·노무']
    },
    { 
      value: 'other', 
      label: '기타',
      subcategories: ['기타']
    },
  ];

  // 선택된 카테고리의 하위 카테고리
  const selectedCategoryData = categories.find(cat => cat.value === formData.category);
  const subcategories = selectedCategoryData ? selectedCategoryData.subcategories : [];

  // 작업 방식 옵션
  const workTypes: { value: string; icon: LucideIcon; title: string; description: string }[] = [
    {
      value: 'outsourcing',
      icon: Package,
      title: t('projectDetail.workOutsourcing'),
      description: t('pjCreate.outsourcingDesc')
    },
    {
      value: 'remote',
      icon: Laptop,
      title: t('pjCreate.workRemote'),
      description: t('pjCreate.remoteDesc')
    },
    {
      value: 'onsite',
      icon: Building2,
      title: t('pjCreate.workOnsite'),
      description: t('pjCreate.onsiteDesc')
    },
    {
      value: 'hybrid',
      icon: Repeat,
      title: t('pjCreate.workHybrid'),
      description: t('pjCreate.hybridDesc')
    },
  ];

  // 입력 핸들러
  const handleInputChange = (field: keyof ProjectFormData, value: any) => {
    console.log(`입력 변경: ${field} = ${value}`);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // 카테고리 변경 시 하위 카테고리 초기화
  const handleCategoryChange = (value: string) => {
    console.log('카테고리 변경:', value);
    setFormData(prev => ({ ...prev, category: value, subcategory: '' }));
  };

  // 스킬 추가
  const addSkill = () => {
    console.log('스킬 추가');
    setFormData(prev => ({ ...prev, requiredSkills: [...prev.requiredSkills, ''] }));
  };

  const removeSkill = (index: number) => {
    console.log(`스킬 제거: ${index}`);
    setFormData(prev => ({
      ...prev,
      requiredSkills: prev.requiredSkills.filter((_, i) => i !== index)
    }));
  };

  const updateSkill = (index: number, value: string) => {
    const newSkills = [...formData.requiredSkills];
    newSkills[index] = value;
    setFormData(prev => ({ ...prev, requiredSkills: newSkills }));
  };

  // 첨부파일 추가
  const addAttachment = () => {
    console.log('첨부파일 추가');
    setFormData(prev => ({ ...prev, attachments: [...prev.attachments, ''] }));
  };

  const removeAttachment = (index: number) => {
    console.log(`첨부파일 제거: ${index}`);
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const updateAttachment = (index: number, value: string) => {
    const newAttachments = [...formData.attachments];
    newAttachments[index] = value;
    setFormData(prev => ({ ...prev, attachments: newAttachments }));
  };

  // 단계별 검증
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!formData.category) {
          setError(t('pjCreate.errCategory'));
          return false;
        }
        break;
      case 2:
        if (!formData.workType) {
          setError(t('pjCreate.errWorkType'));
          return false;
        }
        break;
      case 3:
        if (!formData.title.trim()) {
          setError(t('pjCreate.errTitle'));
          return false;
        }
        if (formData.title.length > 100) {
          setError(t('pjCreate.errTitleLen'));
          return false;
        }
        break;
      case 4:
        if (formData.budgetMin <= 0) {
          setError(t('pjCreate.errBudgetMin'));
          return false;
        }
        if (formData.budgetMax <= 0) {
          setError(t('pjCreate.errBudgetMax'));
          return false;
        }
        if (formData.budgetMin > formData.budgetMax) {
          setError(t('pjCreate.errBudgetOrder'));
          return false;
        }
        if (formData.durationDays <= 0) {
          setError(t('pjCreate.errDuration'));
          return false;
        }
        break;
    }
    setError('');
    return true;
  };

  // 다음 단계
  const handleNext = () => {
    console.log('다음 단계 클릭, 현재:', currentStep);
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // 이전 단계
  const handlePrevious = () => {
    console.log('이전 단계 클릭, 현재:', currentStep);
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 제출
  const handleSubmit = async () => {
    console.log('프로젝트 등록 제출');
    
    if (!user) {
      setError(t('common.loginRequired'));
      return;
    }

    // 최종 검증
    if (!validateStep(4)) {
      return;
    }

    // workType 검증 (추가)
    if (!formData.workType) {
      setError(t('pjCreate.errWorkType'));
      setCurrentStep(2);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 빈 값 필터링
      const filteredSkills = formData.requiredSkills.filter(skill => skill.trim() !== '');
      const filteredAttachments = formData.attachments.filter(att => att.trim() !== '');

      const projectData: ProjectData = {
        category: formData.category,
        subcategory: formData.subcategory || undefined,
        workType: formData.workType as 'outsourcing' | 'remote' | 'onsite' | 'hybrid',
        title: formData.title,
        shortDescription: formData.shortDescription || undefined,
        budgetMin: formData.budgetMin,
        budgetMax: formData.budgetMax,
        budgetType: formData.budgetType,
        startDate: formData.startDate || undefined,
        durationDays: formData.durationDays,
        description: formData.description || undefined,
        requiredSkills: filteredSkills.length > 0 ? filteredSkills : undefined,
        attachments: filteredAttachments.length > 0 ? filteredAttachments : undefined,
        experienceLevel: formData.experienceLevel || undefined,
      };

      console.log('전송할 프로젝트 데이터:', projectData);

      // API 연동
      await projectAPI.createProject(projectData);

      setSuccess(t('pjCreate.success'));
      setShowSuccessModal(true);
      console.log('프로젝트 등록 성공');

      // 3초 후 프로젝트 목록으로 이동
      setTimeout(() => {
        navigate('/projects');
      }, 3000);
    } catch (err: any) {
      console.error('프로젝트 등록 실패:', err);
      setError(err.response?.data?.message || t('pjCreate.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="project-create-container">
      {/* 성공 모달 */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon success"><CheckCircle2 size={48} strokeWidth={1.8} /></div>
            <h2>{t('pjCreate.successTitle')}</h2>
            <p>{t('pjCreate.successMsg')}</p>
            <p className="modal-redirect">{t('pjCreate.redirect')}</p>
            <button 
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/projects');
              }}
              className="modal-button"
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      )}

      {/* 헤더 */}
      <div className="project-create-header">
        <h1><ClipboardList size={24} strokeWidth={2.2} /> {t('pjCreate.title')}</h1>
        <p>{t('pjCreate.subtitle')}</p>
      </div>

      {/* 진행 단계 표시 */}
      <div className="progress-steps">
        {[1, 2, 3, 4, 5].map(step => (
          <div 
            key={step} 
            className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
          >
            <div className="step-number">{step}</div>
            <div className="step-label">
              {step === 1 && t('pjCreate.stepCategory')}
              {step === 2 && t('projectDetail.workType')}
              {step === 3 && t('profile.basicInfo')}
              {step === 4 && t('pjCreate.stepBudget')}
              {step === 5 && t('pjCreate.stepDetails')}
            </div>
          </div>
        ))}
      </div>

      {/* 에러/성공 메시지 */}
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      {/* 폼 섹션 */}
      <div className="project-create-form">
        {/* 1단계: 카테고리 선택 */}
        {currentStep === 1 && (
          <div className="form-step">
            <div className="step-header">
              <span className="step-badge">1</span>
              <h2>{t('pjCreate.s1Title')}</h2>
            </div>

            <div className="form-group">
              <label>{t('pjCreate.mainCategory')} <span className="required">*</span></label>
              <select
                value={formData.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="form-select"
              >
                <option value="">{t('pjCreate.mainCategoryPlaceholder')}</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {categoryLabel(cat.label)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('pjCreate.subCategory')}</label>
              <select
                value={formData.subcategory}
                onChange={(e) => handleInputChange('subcategory', e.target.value)}
                className="form-select"
                disabled={!formData.category}
              >
                <option value="">{t('pjCreate.subCategoryPlaceholder')}</option>
                {subcategories.map(sub => (
                  <option key={sub} value={sub}>
                    {categoryLabel(sub)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* 2단계: 작업 방식 선택 */}
        {currentStep === 2 && (
          <div className="form-step">
            <div className="step-header">
              <span className="step-badge">2</span>
              <h2>{t('pjCreate.s2Title')}</h2>
            </div>

            <div className="work-type-grid">
              {workTypes.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    className={`work-type-card ${formData.workType === type.value ? 'selected' : ''}`}
                    onClick={() => handleInputChange('workType', type.value)}
                  >
                    <div className="work-type-icon"><Icon size={32} strokeWidth={1.8} /></div>
                    <h3>{type.title}</h3>
                    <p>{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 3단계: 기본 정보 */}
        {currentStep === 3 && (
          <div className="form-step">
            <div className="step-header">
              <span className="step-badge">3</span>
              <h2>{t('pjCreate.s3Title')}</h2>
            </div>

            <div className="form-group">
              <label>
                {t('pjCreate.titleLabel')} <span className="required">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder={t('pjCreate.titlePlaceholder')}
                maxLength={100}
                className="form-input"
              />
              <small className="char-count">{formData.title.length}/100</small>
            </div>

            <div className="form-group">
              <label>{t('pjCreate.shortDesc')}</label>
              <textarea
                value={formData.shortDescription}
                onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                placeholder={t('pjCreate.shortDescPlaceholder')}
                maxLength={200}
                rows={3}
                className="form-textarea"
              />
              <small className="char-count">{formData.shortDescription.length}/200</small>
            </div>
          </div>
        )}

        {/* 4단계: 예산 및 기간 */}
        {currentStep === 4 && (
          <div className="form-step">
            <div className="step-header">
              <span className="step-badge">4</span>
              <h2>{t('pjCreate.s4Title')}</h2>
            </div>

            <div className="form-group">
              <label>{t('pjCreate.budgetType')} <span className="required">*</span></label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    checked={formData.budgetType === 'fixed'}
                    onChange={() => handleInputChange('budgetType', 'fixed')}
                  />
                  <span>{t('pjCreate.budgetFixed')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    checked={formData.budgetType === 'hourly'}
                    onChange={() => handleInputChange('budgetType', 'hourly')}
                  />
                  <span>{t('pjCreate.budgetHourly')}</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    checked={formData.budgetType === 'negotiable'}
                    onChange={() => handleInputChange('budgetType', 'negotiable')}
                  />
                  <span>{t('projectDetail.negotiable')}</span>
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  {t('pjCreate.minBudget')} <span className="required">*</span>
                </label>
                <input
                  type="number"
                  value={formData.budgetMin || ''}
                  onChange={(e) => handleInputChange('budgetMin', parseInt(e.target.value) || 0)}
                  placeholder="1000000"
                  min="0"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>
                  {t('pjCreate.maxBudget')} <span className="required">*</span>
                </label>
                <input
                  type="number"
                  value={formData.budgetMax || ''}
                  onChange={(e) => handleInputChange('budgetMax', parseInt(e.target.value) || 0)}
                  placeholder="5000000"
                  min="0"
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('pjCreate.startDate')}</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  min="1920-01-01"
                  max="2099-12-31"
                  className="form-input"
                />
                <small className="input-help">{t('pjCreate.yearRange')}</small>
              </div>

              <div className="form-group">
                <label>
                  {t('pjCreate.duration')} <span className="required">*</span>
                </label>
                <input
                  type="number"
                  value={formData.durationDays || ''}
                  onChange={(e) => handleInputChange('durationDays', parseInt(e.target.value) || 0)}
                  placeholder="30"
                  min="1"
                  max="3650"
                  className="form-input"
                />
                <small className="input-help">{t('pjCreate.maxDuration')}</small>
              </div>
            </div>
          </div>
        )}

        {/* 5단계: 상세 정보 (선택) */}
        {currentStep === 5 && (
          <div className="form-step">
            <div className="step-header">
              <span className="step-badge">5</span>
              <h2>{t('pjCreate.s5Title')}</h2>
            </div>

            <div className="form-group">
              <label>{t('pjCreate.detailDesc')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('pjCreate.descPlaceholder')}
                rows={6}
                className="form-textarea"
              />
            </div>

            <div className="form-group">
              <label>{t('pjCreate.skills')}</label>
              {formData.requiredSkills.map((skill, index) => (
                <div key={index} className="skill-input-group">
                  <input
                    type="text"
                    value={skill}
                    onChange={(e) => updateSkill(index, e.target.value)}
                    placeholder={t('pjCreate.skillPlaceholder')}
                    className="form-input"
                  />
                  {formData.requiredSkills.length > 0 && (
                    <button
                      type="button"
                      onClick={() => removeSkill(index)}
                      className="btn-remove"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addSkill}
                className="btn-add"
              >
                + {t('pjCreate.addSkill')}
              </button>
            </div>

            <div className="form-group">
              <label>{t('pjCreate.attachments')}</label>
              {formData.attachments.map((attachment, index) => (
                <div key={index} className="attachment-input-group">
                  <input
                    type="text"
                    value={attachment}
                    onChange={(e) => updateAttachment(index, e.target.value)}
                    placeholder={t('pjCreate.filePlaceholder')}
                    className="form-input"
                  />
                  {formData.attachments.length > 0 && (
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="btn-remove"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addAttachment}
                className="btn-add"
              >
                + {t('pjCreate.addFile')}
              </button>
            </div>

            <div className="form-group">
              <label>{t('projectDetail.expLevel')}</label>
              <select
                value={formData.experienceLevel}
                onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
                className="form-select"
              >
                <option value="">{t('pjCreate.noSelect')}</option>
                <option value="junior">{t('pjCreate.junior')}</option>
                <option value="intermediate">{t('pjCreate.intermediate')}</option>
                <option value="senior">{t('pjCreate.senior')}</option>
              </select>
            </div>
          </div>
        )}

        {/* 네비게이션 버튼 */}
        <div className="form-navigation">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="btn-previous"
            >
              ← {t('common.prev')}
            </button>
          )}

          {currentStep < 5 ? (
            <button
              type="button"
              onClick={handleNext}
              className="btn-next"
            >
              {t('common.next')} →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="btn-submit"
            >
              {loading ? t('svCreate.submitting') : t('pjCreate.title')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectCreatePage;
